import { debounce } from "lodash";
import { useListFilterContext } from "ra-core";
import { FC, createContext, useContext, useMemo, useState } from "react";
import { PrimaryClassification, SecondaryClassificationResponseDto } from "~/gen/api/governance";
import { FileTagResponseDto, SearchInput } from "~/gen/api/securefile";


export interface FileCataSearchContextProps {
  filters: {
    name: string | null;
    tags: FileTagResponseDto[];
    primaryClassification: PrimaryClassification[];
    secondClassification: SecondaryClassificationResponseDto[];
  },
  searchName: (s: string|null) => void;
  addSearchTag: (t: FileTagResponseDto) => void;
  removeSearchTag: (t: FileTagResponseDto) => void;
  addPrimaryClassification: (t: PrimaryClassification) => void;
  removePrimaryClassification: (t: PrimaryClassification) => void;
  addSecondClassification: (t: SecondaryClassificationResponseDto) => void;
  removeSecondClassification: (t: SecondaryClassificationResponseDto) => void;
}

export const FileCataSearchContext = createContext<FileCataSearchContextProps>({} as any)

export const FileCataSearchContextProvider : FC = ({children}) => {
  const { setFilters, displayedFilters } = useListFilterContext()
  const [states, setStates] = useState({
    name: null as string|null,
    tags: [] as FileTagResponseDto[],
    primaryClassification: [] as PrimaryClassification[],
    secondClassification: [] as SecondaryClassificationResponseDto[],
  })
  const ctrls = useMemo(() => {
    const invokeRequest = (s0: typeof states) => {
      const searchInput : SearchInput = {
        term: !s0.name ? null : s0.name,
        tags: s0.tags.map(x => x.tagId!),
        primaryClassifications: [...s0.primaryClassification],
        secondaryClassifications: s0.secondClassification.map(x => x.secondaryClassificationId!),
      }
      setFilters(searchInput, displayedFilters)
    }
    const debouncedInvokeReq = debounce((s0: typeof states) => {
      invokeRequest(s0)
    }, 500)
    
    return {
      searchName: (s:string|null) => {
        setStates(v => {
          if (v.name !== s) {
            const v1 = {...v, name: s}
            setTimeout(() => debouncedInvokeReq(v1))
            return v1
          } else {
            return v
          }
        })
      },
      addSearchTag: (t:FileTagResponseDto) => {
        setStates(v => {
          if (v.tags.every(x => x.tagId !== t.tagId)) {
            const v1 = {...v, tags: [...v.tags, t]}
            setTimeout(() => invokeRequest(v1))
            return v1
          } else {
            return v
          }
        })
      },
      removeSearchTag: (t:FileTagResponseDto) => {
        setStates(v => {
          const i = v.tags.findIndex(x => x.tagId === t.tagId)
          if (i > -1) {
            const tags = [...v.tags]
            tags.splice(i, 1)
            const v1 = {...v, tags}
            setTimeout(() => invokeRequest(v1))
            return v1
          } else {
            return v
          }
        })
      },
      addPrimaryClassification: (t:PrimaryClassification) => {
        setStates(v => {
          if (v.primaryClassification.every(x => x !== t)) {
            const v1 = {...v, primaryClassification: [...v.primaryClassification, t]}
            setTimeout(() => invokeRequest(v1))
            return v1
          } else {
            return v
          }
        })
      },
      removePrimaryClassification: (t:PrimaryClassification) => {
        setStates(v => {
          const i = v.primaryClassification.findIndex(x => x === t)
          if (i > -1) {
            const primaryClassification = [...v.primaryClassification]
            primaryClassification.splice(i, 1)
            const v1 = {...v, primaryClassification}
            setTimeout(() => invokeRequest(v1))
            return v1
          } else {
            return v
          }
        })
      },
      addSecondClassification: (t:SecondaryClassificationResponseDto) => {
        setStates(v => {
          if (v.secondClassification.every(x => x.secondaryClassificationId !== t.secondaryClassificationId)) {
            const v1 = {...v, secondClassification: [...v.secondClassification, t]}
            setTimeout(() => invokeRequest(v1))
            return v1
          } else {
            return v
          }
        })
      },
      removeSecondClassification: (t:SecondaryClassificationResponseDto) => {
        setStates(v => {
          const i = v.secondClassification.findIndex(x => x.secondaryClassificationId === t.secondaryClassificationId)
          if (i > -1) {
            const secondClassification = [...v.secondClassification]
            secondClassification.splice(i, 1)
            const v1 = {...v, secondClassification}
            setTimeout(() => invokeRequest(v1))
            return v1
          } else {
            return v
          }
        })
      }
    }
  }, [setFilters, displayedFilters])
  
  return (
    <FileCataSearchContext.Provider value={{...ctrls, filters: states}}>
      {children}
    </FileCataSearchContext.Provider>
  )
}

export const useFileCataSearchContext = () => useContext(FileCataSearchContext)
